/**
 * Utility functions for handling mastery data in IRIP documents
 * These functions are shared across multiple IRIP sections to ensure consistency
 */
import { MasteryLevel, MasteryPacing, MasteryPacingColor, MasteryStatusPacing } from "@/enums";

/**
 * Gets the hex color code for a mastery pacing level
 * Uses the same colors as MasteryPacingColor enum and cutscores.js
 * @param {string} pacing - The mastery pacing level
 * @returns {string} Hex color code without # prefix
 */
export const getMasteryColor = (pacing) => {
  if (!pacing) return MasteryPacingColor.NO_DATA.substring(1); // Remove # prefix
  return MasteryPacingColor[pacing]?.substring(1) || MasteryPacingColor.NO_DATA.substring(1); // Remove # prefix
};

/**
 * Converts a mastery pacing level to a human-readable status
 * @param {string} pacing - The mastery pacing level
 * @returns {string} Human-readable mastery status
 */
export const getMasteryStatus = (pacing) => {
  if (!pacing) return "No Data";
  
  switch (pacing) {
    case MasteryPacing.AHEAD_OF_GRADE_LEVEL:
      return "Likely Mastered";
    case MasteryPacing.AT_GRADE_LEVEL:
      return "Developing";
    case MasteryPacing.BELOW_GRADE_LEVEL:
      return "Not Developed";
    default:
      return "No Data";
  }
};

/**
 * Converts a mastery pacing level to a numeric level for comparison
 * Higher numbers indicate better mastery
 * @param {string} pacing - The mastery pacing level
 * @returns {number} Numeric mastery level (0-3)
 */
export const getMasteryLevel = (pacing) => {
  if (!pacing) return 0;
  
  switch (pacing) {
    case MasteryPacing.AHEAD_OF_GRADE_LEVEL:
      return 3;
    case MasteryPacing.AT_GRADE_LEVEL:
      return 2;
    case MasteryPacing.BELOW_GRADE_LEVEL:
      return 1;
    default:
      return 0;
  }
};

/**
 * Converts a mastery pacing level to a simplified color category
 * Used for analysis and summary generation
 * @param {string} pacing - The mastery pacing level
 * @returns {string} Color category: "red", "yellow", "green", or "unknown"
 */
export const getMasteryColorCategory = (pacing) => {
  if (!pacing) return MasteryLevel.UNKNOWN;
  return MasteryLevel[pacing] || MasteryLevel.UNKNOWN;
};

/**
 * Converts a MasteryPacing enum value to a MasteryStatusPacing enum value
 * Used for transforming mastery data between different enum representations
 * Primarily for getting the translation key for the mastery status
 * i.e. "NOT_DEVELOPED" -> "undeveloped"
 * @param {string|null} pacing - The MasteryPacing enum value or null
 * @returns {string} The corresponding MasteryStatusPacing enum value
 */
export const getMasteryKey = (pacing) => {
  if (!pacing) {
    return MasteryStatusPacing.NO_DATA;
  }
  
  switch (pacing) {
    case MasteryPacing.AHEAD_OF_GRADE_LEVEL:
      return MasteryStatusPacing.LIKELY_MASTERED;
    case MasteryPacing.AT_GRADE_LEVEL:
      return MasteryStatusPacing.DEVELOPING;
    case MasteryPacing.BELOW_GRADE_LEVEL:
      return MasteryStatusPacing.NOT_DEVELOPED;
    default:
      return MasteryStatusPacing.NO_DATA;
  }
}; 